import { useSelector } from 'react-redux'
import { vn } from 'vend-number'
import { ApiRetailerPaymentType } from '@vend/types'
import { PAYMENT_TYPES } from '@vend/business-components'
import { useCallback, useEffect, useState } from 'react'
import {
  useRegisterManager,
  useWebRegisterStatusManager,
} from '../../../../ng-compat/hooks'
import { selectCurrentSale } from '../../../../state/slices/currentSale/currentSaleSlice'
import {
  shouldUseDirectEconduit,
  shouldDisableEconduitPayment,
} from '../../../../functions/payment-types/econduit'
import {
  isKlarnaPaymentType,
  shouldDisableKlarnaPayment,
} from '../../../../functions/payment-types/klarna'
import { shouldDisableOnAccountPayment } from '../../../../functions/payment-types/onaccount'
import { requireUnreferencedRefund } from '../../../../functions/payment-types/lspay'
import { shouldPreventOtherPayments } from '../../../../functions/payment-types/other-payment'
import { shouldDisableVCloudPayment } from '../../../../functions/payment-types/vcloud'
import {
  getOutstandingBalance,
  isAccountBalancePaymentAdded,
  isAccountBalancePaymentPaid,
  isReturn,
} from '../../../../functions/sale'
import { shouldAllowUnreferencedRefunds } from '../../../payments/lspay-payment-modal/helpers/refund'
import { getFeatureFromCache } from '../../../../util/feature-cache'
import { EVENTS } from '../../../../constants'

/**
 * @returns Function Responsible for determining whether webregister is in offline only mode
 * and if the payment type in question should be disabled accordingly.
 */
export const useCheckDisablePaymentInOfflineMode = () => {
  const webRegisterStatus = useWebRegisterStatusManager()
  const [isOnline, setIsOnline] = useState(webRegisterStatus.isOnline())

  useEffect(() => {
    const checkWebRegisterStatus = () => {
      setIsOnline(webRegisterStatus.isOnline())
    }
    webRegisterStatus.on(EVENTS.statusChange, checkWebRegisterStatus)
    return () => {
      webRegisterStatus.off(EVENTS.statusChange, checkWebRegisterStatus)
    }
  }, [webRegisterStatus])

  const checkPaymentDisabledOfflineMode = useCallback(
    (paymentType: ApiRetailerPaymentType): boolean => {
      const isOnlineOnly =
        paymentType.payment_type && paymentType.payment_type.online_only
      const isInOfflineMode = !isOnline
      return isOnlineOnly && isInOfflineMode
    },
    [isOnline]
  )

  return checkPaymentDisabledOfflineMode
}

/**
 * Hook to check if a payment type should be disabled
 *
 * @param amountTendered The current amount tendered
 * @param hasValidTenderedAmount Whether the tendered amount is valid
 * @returns A function that takes a payment type and returns whether it should be disabled
 */
export const usePaymentDisabilityCheck = (
  amountTendered: number | string,
  hasValidTenderedAmount: boolean
) => {
  const sale = useSelector(selectCurrentSale)
  const registerManager = useRegisterManager()
  const disableOnlinePaymentInOfflineMode = useCheckDisablePaymentInOfflineMode()

  /**
   * Checks if a payment type should be disabled based on various criteria
   * @returns True if the payment should be disabled, false otherwise
   */
  const checkIfPaymentDisabled = useCallback(
    (paymentType: ApiRetailerPaymentType): boolean => {
      // Check if online payment should be disabled in offline mode
      const isOnlineOnly = disableOnlinePaymentInOfflineMode(paymentType)
      if (isOnlineOnly) {
        return true
      }

      // Check if tendered amount is valid
      if (!hasValidTenderedAmount) {
        return true
      }

      // Check if direct econduit payment should be disabled
      if (
        shouldUseDirectEconduit(paymentType) &&
        getFeatureFromCache('direct_econduit')
      ) {
        return shouldDisableEconduitPayment(paymentType, sale)
      }

      // Check if Klarna payment should be disabled
      if (isKlarnaPaymentType(paymentType.type_id)) {
        return shouldDisableKlarnaPayment(amountTendered, sale)
      }

      // Check specific payment types
      switch (paymentType.type_id) {
        case PAYMENT_TYPES.LSPAY: {
          // 0 amount cannot be processed by lspay
          if (vn(amountTendered).eq(0)) {
            return true
          }

          // If current transaction requires unref refund but unref refund is not enabled for current store, disable lspay
          return (
            requireUnreferencedRefund(amountTendered, sale) &&
            !shouldAllowUnreferencedRefunds(
              getFeatureFromCache('lspay_unreferenced_refund'),
              getFeatureFromCache('lspay_international_unreferenced_refunds'),
              registerManager.getActiveOutlet().currency
            )
          )
        }

        case PAYMENT_TYPES.LIGHTSPEED_ECOM:
          if (vn(amountTendered).eq(0)) {
            return true
          }
          break

        case PAYMENT_TYPES.VCLOUD:
          return shouldDisableVCloudPayment(amountTendered, sale)

        case PAYMENT_TYPES.BIGCOMMERCE_GIFT_CERT:
          // We don't allow returns of BigCommerce Gift Certs at the moment.
          if (vn(getOutstandingBalance(sale)).lte(0)) {
            return true
          }
          break

        case PAYMENT_TYPES.GIFT_CARD:
          if (vn(getOutstandingBalance(sale)).lte(0)) {
            return true
          } else if (
            isAccountBalancePaymentAdded(sale.payAccountBalance) &&
            !isAccountBalancePaymentPaid(sale.payAccountBalance)
          ) {
            // Cannot make an account balance payment with a gift card as both the sale and ABP will fail.
            return true
          }
          break

        case PAYMENT_TYPES.ON_ACCOUNT:
          if (
            shouldDisableOnAccountPayment({
              customer: sale.customer,
              amountTendered,
              sale,
            })
          ) {
            return true
          }
          break

        case PAYMENT_TYPES.PAYJUNCTION:
          // We don't actually support partial refunds for integrated payments right now when the refund is tokenised.
          // If it's a blind refund then we can allow the amount each time, the consumer chooses the card.
          // But if it's based on original payment ID then currently we prevent it
          if (isReturn(sale) && sale.originalSalePayments.length > 1) {
            return true
          }
          break
      }
      // Check if other payments should be prevented
      return shouldPreventOtherPayments(amountTendered, sale)
    },
    [
      amountTendered,
      disableOnlinePaymentInOfflineMode,
      hasValidTenderedAmount,
      registerManager,
      sale,
    ]
  )

  return { checkIfPaymentDisabled }
}
